import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { personalInfo } from '../../../../config/personalInfo';
import { sendContactMessage } from './contactApi';

const fieldClass =
    'w-full border border-white/15 bg-ink px-4 py-3 text-fg transition-colors placeholder:text-muted/70 focus:border-accent focus:outline-none';

const Contact = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: '',
        website: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | ''>('');
    const [submitError, setSubmitError] = useState('');
    const { t } = useLanguage();

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 },
        );

        const element = document.getElementById('contact');
        if (element) {
            observer.observe(element);
        }

        return () => {
            if (element) {
                observer.unobserve(element);
            }
        };
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus('');
        setSubmitError('');

        try {
            const result = await sendContactMessage({
                name: formData.name,
                email: formData.email,
                subject: formData.subject,
                message: formData.message,
                website: formData.website,
            });

            if (!result.ok) {
                setSubmitStatus('error');
                setSubmitError(result.error || t('contact.form.error'));
                return;
            }

            setSubmitStatus('success');
            setFormData({ name: '', email: '', subject: '', message: '', website: '' });
            window.setTimeout(() => setSubmitStatus(''), 4000);
        } catch {
            setSubmitStatus('error');
            setSubmitError(t('contact.form.error'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const contactInfo = [
        {
            title: t('contact.contactInfo.0.title'),
            value: personalInfo.email,
            link: personalInfo.social.email,
        },
        {
            title: t('contact.contactInfo.1.title'),
            value: personalInfo.phone,
            link: `tel:${personalInfo.phone?.replace(/\D/g, '') || ''}`,
        },
        {
            title: t('contact.contactInfo.2.title'),
            value: personalInfo.location,
            link: '#',
        },
    ];

    const socialLinks = [
        { name: 'GitHub', url: personalInfo.social.github },
        { name: 'LinkedIn', url: personalInfo.social.linkedin },
        { name: 'Twitter', url: personalInfo.social.twitter },
    ];

    return (
        <section id="contact" className="bg-surface py-24">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div
                    className={`transition-all duration-700 ${
                        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                    }`}
                >
                    <div className="mb-16 max-w-3xl">
                        <h2 className="mb-4 font-display text-4xl font-bold tracking-tight text-fg md:text-5xl">
                            <span className="text-accent">{t('contact.title')}</span>
                        </h2>
                        <p className="text-xl text-muted">{t('contact.subtitle')}</p>
                    </div>

                    <div className="grid grid-cols-1 gap-14 lg:grid-cols-2">
                        <div className="space-y-8">
                            <div>
                                <h3 className="mb-4 font-display text-2xl font-bold text-fg">
                                    {t('contact.info.title')}
                                </h3>
                                <p className="mb-8 leading-relaxed text-muted">
                                    {t('contact.info.description')}
                                </p>
                            </div>

                            <div className="space-y-4">
                                {contactInfo.map(
                                    (info, index) =>
                                        info.value && (
                                            <a
                                                key={index}
                                                href={info.link || '#'}
                                                className="block border border-white/10 bg-ink p-5 transition-colors hover:border-accent"
                                            >
                                                <h4 className="mb-1 font-semibold text-fg">
                                                    {info.title}
                                                </h4>
                                                <p className="text-muted">{info.value}</p>
                                            </a>
                                        ),
                                )}
                            </div>

                            <div>
                                <h4 className="mb-4 font-semibold text-fg">
                                    {t('contact.info.socialTitle')}
                                </h4>
                                <div className="flex flex-wrap gap-3">
                                    {socialLinks
                                        .filter((social) => social.url)
                                        .map((social, index) => (
                                            <a
                                                key={index}
                                                href={social.url!}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="border border-white/15 px-4 py-2 text-sm text-muted transition-colors hover:border-accent hover:text-accent"
                                            >
                                                {social.name}
                                            </a>
                                        ))}
                                </div>
                            </div>
                        </div>

                        <div className="border border-white/10 bg-ink p-8">
                            <h3 className="mb-6 font-display text-2xl font-bold text-fg">
                                {t('contact.form.title')}
                            </h3>

                            {submitStatus === 'success' && (
                                <div className="mb-6 border border-accent/50 bg-accent/10 p-4">
                                    <p className="text-accent-soft">{t('contact.form.success')}</p>
                                </div>
                            )}
                            {submitStatus === 'error' && (
                                <div className="mb-6 border border-highlight/50 bg-highlight/10 p-4">
                                    <p className="text-highlight-soft">
                                        {submitError || t('contact.form.error')}
                                    </p>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="relative space-y-6">
                                <div className="pointer-events-none absolute left-[-10000px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
                                    <label htmlFor="website">Website</label>
                                    <input
                                        type="text"
                                        id="website"
                                        name="website"
                                        tabIndex={-1}
                                        autoComplete="off"
                                        value={formData.website}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                    <div>
                                        <label htmlFor="name" className="mb-2 block text-sm text-muted">
                                            {t('contact.form.fields.name')} *
                                        </label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            required
                                            className={fieldClass}
                                            placeholder={t('contact.form.placeholders.name')}
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="email" className="mb-2 block text-sm text-muted">
                                            {t('contact.form.fields.email')} *
                                        </label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            required
                                            className={fieldClass}
                                            placeholder={t('contact.form.placeholders.email')}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="subject" className="mb-2 block text-sm text-muted">
                                        {t('contact.form.fields.subject')} *
                                    </label>
                                    <input
                                        type="text"
                                        id="subject"
                                        name="subject"
                                        value={formData.subject}
                                        onChange={handleInputChange}
                                        required
                                        className={fieldClass}
                                        placeholder={t('contact.form.placeholders.subject')}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="message" className="mb-2 block text-sm text-muted">
                                        {t('contact.form.fields.message')} *
                                    </label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        value={formData.message}
                                        onChange={handleInputChange}
                                        required
                                        rows={6}
                                        className={`${fieldClass} resize-none`}
                                        placeholder={t('contact.form.placeholders.message')}
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full bg-accent py-4 font-semibold text-fg transition-colors hover:bg-accent-soft disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {isSubmitting ? t('contact.form.sending') : t('contact.form.button')}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export { Contact };
